/**
 * Golem role library (fork addition, FT-851) — save/load/fork CUSTOM ROLES
 * against the golem server, plus the localStorage "recents" shelf. Mirrors
 * the script vault (scripts.js): same anonymous edit-link model, same shelf
 * semantics — the secret EDIT KEY is returned exactly once on create and kept
 * only here; a keyless save of someone else's role FORKS it (the server
 * answers 403 {hint:"fork"}), recording the original as its parent.
 *
 * Wire notes (the roles API speaks towns-FLAT, not the scripts {script:...}
 * wrapper): POST → 201 flat row + editKey; GET /:id and PUT /:id → flat row;
 * GET / → {roles:[...]}. The server spells the fifth type "traveller"; the
 * app spells it "traveler" — map at this boundary, both directions.
 */

// FT-1480: a library row's own night wording (`night.copy`) is washed through
// the copy layer's one sanitizer before it rides a script snapshot.
import { sanitizeCopyEntry } from "./nightSigns";
// FT-1480 (botc FT-1475 part 2): …and its steps become the composed night
// entry the script snapshot carries (golemNight), registered at every script
// landing.
import { entryFromSteps } from "./nightInfo";

const API =
  process.env.NODE_ENV === "development"
    ? "/api/lots/roles" // the dev server proxies /api (vue.config.js, FT-1010)
    : "/api/lots/roles";

const SHELF_KEY = "golem.roles";
const SHELF_MAX = 30;

/** The recents shelf: [{id, name, editKey?, role, lastSeen}] newest first. */
export function getRecents() {
  try {
    const raw = JSON.parse(localStorage.getItem(SHELF_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
}

/** Upsert a shelf entry by id. `role` upgrades (viewed < forked < created). */
export function remember(entry) {
  const rank = { viewed: 0, forked: 1, created: 2 };
  const shelf = getRecents().filter(e => e.id !== entry.id);
  const prior = getRecents().find(e => e.id === entry.id) || {};
  shelf.unshift({
    ...prior,
    ...entry,
    // never DOWNGRADE a role or lose a stored edit key on a later view
    role:
      (rank[entry.role] || 0) >= (rank[prior.role] || 0)
        ? entry.role
        : prior.role,
    editKey: entry.editKey || prior.editKey,
    lastSeen: Date.now()
  });
  localStorage.setItem(SHELF_KEY, JSON.stringify(shelf.slice(0, SHELF_MAX)));
}

/** The stored edit key for a library role id, if this browser holds one. */
export function editKeyFor(id) {
  const entry = getRecents().find(e => e.id === id);
  return entry && entry.editKey;
}

/** A plain-text dump of the shelf for "export my links" (clipboard-bound). */
export function exportLinks() {
  return getRecents()
    .map(e => {
      const key = e.editKey ? `  (edit key: ${e.editKey})` : "";
      return `${e.name} — role id: ${e.id}${key}`;
    })
    .join("\n");
}

/** server 'traveller' → app 'traveler'; the other four pass through. */
export function teamFromRoleType(roleType) {
  return roleType === "traveller" ? "traveler" : roleType;
}

/** app 'traveler' → server 'traveller'; the other four pass through. */
export function roleTypeFromTeam(team) {
  return team === "traveler" ? "traveller" : team;
}

/** Read the server's error body into a human sentence (zod details first). */
async function apiMessage(res, fallback) {
  try {
    const data = await res.json();
    if (data && Array.isArray(data.details) && data.details.length) {
      const issue = data.details[0];
      const path = Array.isArray(issue.path) ? issue.path.join(".") : "";
      return path ? `${path}: ${issue.message}` : issue.message;
    }
    if (data && data.error) return data.error;
  } catch (e) {
    /* fall through to the fallback */
  }
  return fallback;
}

/** GET a library role. Returns the flat row; remembers it as viewed. */
export async function loadRole(id) {
  const res = await fetch(`${API}/${id}`);
  if (!res.ok) throw new Error(`role not found (${res.status})`);
  const role = await res.json();
  remember({ id: role.id, name: role.name, role: "viewed" });
  return role;
}

/** Browse the library. Filters compose; returns the flat rows newest-first. */
export async function browseRoles({ q, type, author, limit } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (type) params.set("type", type);
  if (author) params.set("author", author);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  const res = await fetch(qs ? `${API}?${qs}` : API);
  if (!res.ok) throw new Error(await apiMessage(res, `browse failed (${res.status})`));
  const { roles } = await res.json();
  return roles;
}

/**
 * Save a role to the library. Decides create / update / fork from what this
 * browser holds (the scripts.js decision, verbatim):
 *   - a stored edit key for `sourceId` → PUT in place (a 403 falls through to
 *     fork — the key was revoked or wrong);
 *   - otherwise → POST, with `sourceId` as parentId when the role came from
 *     the library (the FORK) or none when it is brand new.
 * `fields` = {name, roleType, ability, shadowAbility, icon, firstNight,
 * otherNight, reminders, setup, authorName, night}. `shadowAbility` (FT-1467)
 * is L.O.T.S.'s Shadow side; it rides through untouched like every other
 * field. Returns { role, created, forked }.
 *
 * FT-1480: `night` is the role's own night data — documented shape
 * `{ copy: {line, invite, sealed, telling, settled, seats, told},
 * reminders: {first, other}, steps: [], spentToken, look }`. The Role Studio
 * authors `copy` (its Words section) and passes the rest through as the row
 * gave it. A server without the column drops it; nothing here depends on it
 * coming back.
 *
 * FT-1480: `owned` = the signed-in account owns row `sourceId` (an OFFICIAL
 * character the house edits). That save is a keyless PUT riding the session
 * cookie, answered in place (`inPlace: true`). It never forks and never lands
 * on the recents shelf — a refusal means the session is no longer the owner's,
 * and that is said, not quietly turned into a custom copy.
 */
export async function saveRole({ sourceId, owned, ...fields }) {
  if (owned && sourceId) {
    const res = await fetch(`${API}/${sourceId}`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    });
    if (!res.ok) {
      throw new Error(
        res.status === 403
          ? "you're not signed in as this character's owner any more"
          : await apiMessage(res, `save failed (${res.status})`),
      );
    }
    const role = await res.json();
    return { role, created: false, forked: false, inPlace: true };
  }
  const key = sourceId && editKeyFor(sourceId);
  if (key) {
    const res = await fetch(`${API}/${sourceId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "x-lots-edit-key": key },
      body: JSON.stringify(fields)
    });
    if (res.ok) {
      const role = await res.json();
      remember({ id: role.id, name: role.name, role: "created" });
      return { role, created: false, forked: false };
    }
    if (res.status !== 403) {
      throw new Error(await apiMessage(res, `save failed (${res.status})`));
    }
    // fall through: key no longer valid → fork
  }
  const body = { ...fields };
  if (sourceId) body.parentId = sourceId;
  // FT-1484: the session cookie rides the create, so a signed-in creator
  // OWNS the new Lot (edits it in place from any device); the key still comes
  // back and is shelved, the fallback when signed out.
  const res = await fetch(API, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await apiMessage(res, `save failed (${res.status})`));
  const { editKey, ...role } = await res.json();
  remember({
    id: role.id,
    name: role.name,
    editKey,
    role: sourceId ? "forked" : "created",
    // already the account's — nothing for the sign-in claim to do
    ...(role.ownedByYou ? { claimed: true } : {}),
  });
  return { role, created: !sourceId, forked: !!sourceId };
}

/**
 * FT-1484: does the signed-in account own library Lot `id`? Asked when a
 * custom Lot opens in the editor, so Save can take the owner path (in place,
 * no key) on any device. False on any failure — a wrong "no" only means the
 * old key-or-fork save. Mirrors botc.
 */
export async function ownsLibraryRole(id) {
  try {
    const res = await fetch(`${API}/${encodeURIComponent(id)}`, {
      credentials: "same-origin",
    });
    if (!res.ok) return false;
    const row = await res.json();
    return !!(row && row.ownedByYou);
  } catch (e) {
    return false;
  }
}

/** FT-1484: mark a shelf entry as settled for the claim, in place (no
 *  reordering, no lastSeen bump — it was not viewed). */
function markClaimed(id) {
  const shelf = getRecents();
  const entry = shelf.find((e) => e.id === id);
  if (!entry) return;
  entry.claimed = true;
  try {
    localStorage.setItem(SHELF_KEY, JSON.stringify(shelf));
  } catch (e) {
    /* storage full or blocked — the next sign-in simply asks again */
  }
}

let claiming = null;

/**
 * FT-1484: CLAIM THE SHELF. After sign-in, and on load while signed in,
 * present every edit key this browser holds (`POST /:id/claim`) so the Lots
 * it made signed-out join the account. Quiet: no UI, never throws. Each entry
 * is asked once — a claim that lands, a row already this account's, or a
 * final refusal (someone else's, a wrong key, gone) marks it `claimed`; a
 * signed-out or unreachable answer leaves the rest for next time. The key
 * stays on the shelf, the fallback when signed out. Mirrors botc.
 */
export function claimShelf(accountId) {
  if (!accountId) return Promise.resolve(0);
  if (claiming) return claiming;
  claiming = (async () => {
    let claimed = 0;
    const todo = getRecents().filter((e) => e.editKey && !e.claimed);
    for (const entry of todo) {
      let res;
      try {
        res = await fetch(`${API}/${encodeURIComponent(entry.id)}/claim`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "x-lots-edit-key": entry.editKey },
        });
      } catch (e) {
        break; // unreachable — the rest wait for the next load
      }
      if (res.status === 401) break; // signed out under us
      if (res.ok) claimed += 1;
      if (res.ok || [403, 404, 409].includes(res.status)) markClaimed(entry.id);
    }
    return claimed;
  })().finally(() => {
    claiming = null;
  });
  return claiming;
}

/**
 * FT-1484: claim the shelf whenever an account arrives — the boot's own
 * `/me` answer (signed in on load) and every later sign-in or sign-up. Bind
 * BEFORE `initAccount` so the boot answer is caught.
 */
export function bindRoleClaims(store) {
  store.watch(
    (state) => (state.session.account ? state.session.account.id : null),
    (id) => {
      if (id) claimShelf(id);
    },
  );
}

/**
 * A library row → the app's custom-role object (the upstream custom-script
 * shape), SNAPSHOT semantics — the script carries the whole role:
 *   - team uses the app's spelling ('traveller' → 'traveler');
 *   - `image` resolves the chosen icon (an official role id) to that role's
 *     bundled image URL — shown when the grimoire's image opt-in is on;
 *   - `golemIcon` carries the official id so the store maps imageAlt to the
 *     bundled icon even WITHOUT the opt-in (see store setCustomRoles);
 *   - `golemRoleId` carries the library id for future edits/stats — harmless
 *     to upstream consumers.
 */
export function toAppRole(role) {
  const id = ("golem" + role.id).toLocaleLowerCase().replace(/[^a-z0-9]/g, "");
  let image = "";
  if (role.icon) {
    try {
      image = require("../assets/icons/" + role.icon + ".png");
    } catch (e) {
      image = ""; // an unknown icon id degrades to the team-generic token
    }
  }
  const out = {
    id,
    name: role.name,
    team: teamFromRoleType(role.roleType),
    ability: role.ability,
    // FT-1467: the Shadow side. The reader (golem/lot.js) falls back to
    // `ability` when it is empty, so a Light-only Lot degrades cleanly.
    shadowAbility: role.shadowAbility || "",
    image,
    golemIcon: role.icon || "",
    golemRoleId: role.id,
    edition: "custom",
    firstNight: role.firstNight || 0,
    otherNight: role.otherNight || 0,
    reminders: Array.isArray(role.reminders) ? role.reminders.slice() : [],
    setup: !!role.setup
  };
  // FT-1480: the role's own night WORDS ride the snapshot as golemNightCopy
  // (the store registers them on every script landing — nightSigns' role
  // copy layer). Only when the row carries some.
  const night =
    role.night && typeof role.night === "object" ? role.night : null;
  const copy = night && sanitizeCopyEntry(night.copy);
  if (copy) out.golemNightCopy = copy;
  applyRoleNight(out, night);
  return out;
}

/**
 * FT-1480 (botc FT-1475 part 2): a role's stored night onto its APP role (the
 * script snapshot), in the keys every runtime surface already reads:
 *   golemRoleNight   the row's whole night, so the Studio re-opens it;
 *   golemNight       the steps composed into a schema entry (only for a role
 *                    that wakes and has at least one step — no steps keeps
 *                    it an ordinary unlisted role, the free-box fallback);
 *   first/otherNightReminder  the night-sheet reminder text;
 *   golemSpentToken  offers an "Ability spent" reminder token.
 * Keys a night does not carry are removed, so a re-save is authoritative.
 */
export function applyRoleNight(out, night) {
  const n = night && typeof night === "object" ? night : null;
  if (n) out.golemRoleNight = JSON.parse(JSON.stringify(n));
  else delete out.golemRoleNight;
  const wakes = [];
  if (out.firstNight > 0) wakes.push("first");
  if (out.otherNight > 0) wakes.push("other");
  const steps = n && Array.isArray(n.steps) ? n.steps : [];
  const entry =
    wakes.length && steps.length
      ? entryFromSteps(steps, { wakes, wakesWhenDead: n.wakesWhenDead })
      : null;
  if (entry) out.golemNight = entry;
  else delete out.golemNight;
  const rem = (n && n.reminders) || {};
  if (rem.first) out.firstNightReminder = rem.first;
  else delete out.firstNightReminder;
  if (rem.other) out.otherNightReminder = rem.other;
  else delete out.otherNightReminder;
  if (n && n.spentToken === true) out.golemSpentToken = true;
  else delete out.golemSpentToken;
  return out;
}
